import dayjs from "dayjs";

export interface V3UserProfile {
  id: number;
  uuid: string;
  nickname?: string;
  avatar?: string;
  email?: string;
  role?: string;
  status?: number;
  balance?: number;
  vip_level?: number;
  vip_expire_at?: string;
  created_at?: string;
}

export interface LegacyUserCompatInfo {
  open_id?: number;
  email?: string;
  role?: string;
  postchat?: {
    api_key?: string;
    vip_info?: {
      start_time?: string;
      end_time?: string;
      level?: number;
      is_vip?: boolean;
    };
  };
  oauth_info?: {
    qq_bind?: boolean;
    github_bind?: boolean;
  };
  invite?: {
    is_invited?: boolean;
    invite_reward_valid?: boolean;
    invited_at?: string;
    invite_remaining_days?: number;
  };
}

export interface V3InviteInfo {
  invite_code?: string;
  invite_count?: number;
  reward_total?: number;
}

export interface V3ProjectListItem {
  id: number;
  name: string;
  key_value: string;
  refer?: string;
  url_binding?: boolean;
  created_at?: string;
  multi_language?: boolean;
  is_postchat?: boolean;
  is_public?: boolean;
  prompt?: string;
  stats?: {
    token_used?: number;
    use_count?: number;
    summary_count?: number;
    knowledge_count?: number;
  };
  api_keys?: Array<{
    id: number;
    key: string;
    name?: string;
    created_at?: string;
  }>;
}

export interface V3ProjectDetail {
  project?: {
    config?: Record<string, unknown>;
  };
}

export interface V3Product {
  id: number;
  product_code: string;
  product_name: string;
  type: number;
  value: number;
  product_price: number;
  original_price?: number;
  product_desc?: string;
  detail_info?: string;
  image_url?: string;
  vip_level?: number;
  discount_type?: string;
  discount_rate?: number;
  discount_desc?: string;
  discount_start?: string;
  discount_end?: string;
  reward_product?: string;
  status?: boolean;
  sort?: number;
  created_at?: string;
  updated_at?: string;
}

export interface V3Order {
  id: number;
  order_id: string;
  order_subject?: string;
  status?: number;
  order_amount: number;
  pay_amount?: number;
  pay_type?: string;
  pay_status?: number;
  pay_id?: string;
  pay_time?: string | null;
  user_id?: number;
  product_id?: number;
  refund_status?: number;
  refund_amount?: number;
  remark?: string;
  created_at: string;
  updated_at?: string;
}

const defaultAvatar = "/assets/images/hongmoai-logo-white";

const safeNumber = (value: unknown, fallback = 0) => {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : fallback;
};

const formatDisplayDate = (value?: string) => {
  if (!value || !dayjs(value).isValid()) {
    return undefined;
  }
  return dayjs(value).format("YYYY/MM/DD");
};

const getRemainingDays = (value?: string) => {
  if (!value || !dayjs(value).isValid()) {
    return undefined;
  }

  const diffDays = dayjs(value)
    .startOf("day")
    .diff(dayjs().startOf("day"), "day");

  return diffDays >= 0 ? diffDays : 0;
};

const inferVipLevel = (product: V3Product) => {
  if (product.type !== 2) {
    return undefined;
  }

  if (product.product_code?.toUpperCase().startsWith("P2") || product.value >= 365) {
    return 2;
  }

  return 1;
};

export const normalizeV3User = (
  profile: V3UserProfile,
  legacyCompat?: LegacyUserCompatInfo | null,
  inviteInfo?: V3InviteInfo | null
) => {
  const vipLevel = safeNumber(profile.vip_level, 0);
  const vipExpireAt =
    profile.vip_expire_at || legacyCompat?.postchat?.vip_info?.end_time || "";
  const isVip = vipLevel > 0 && !!vipExpireAt && dayjs(vipExpireAt).isAfter(dayjs());

  return {
    uid: String(profile.id || legacyCompat?.open_id || profile.uuid || ""),
    avatar: profile.avatar || defaultAvatar,
    memberStatus: isVip
      ? ("PostChat会员" as const)
      : ("普通用户" as const),
    expiryDate: isVip ? formatDisplayDate(vipExpireAt) : undefined,
    remainingDays: isVip ? getRemainingDays(vipExpireAt) : undefined,
    apiInfo: {
      open_id: legacyCompat?.open_id || profile.id || 0,
      email: profile.email || legacyCompat?.email || "",
      postchat: {
        api_key: legacyCompat?.postchat?.api_key || "",
        vip_info: {
          is_vip: isVip,
          start_time: legacyCompat?.postchat?.vip_info?.start_time || "",
          end_time: vipExpireAt,
          level: vipLevel,
        },
        token: safeNumber(profile.balance, 0),
      },
      oauth_info: {
        qq_bind: !!legacyCompat?.oauth_info?.qq_bind,
        github_bind: !!legacyCompat?.oauth_info?.github_bind,
      },
      role: profile.role || legacyCompat?.role || "user",
      invite: {
        is_invited: !!legacyCompat?.invite?.is_invited,
        invite_reward_valid: !!legacyCompat?.invite?.invite_reward_valid,
        invited_at: legacyCompat?.invite?.invited_at || "",
        invite_code: inviteInfo?.invite_code || "",
        invite_count: safeNumber(inviteInfo?.invite_count, 0),
        reward_total: safeNumber(inviteInfo?.reward_total, 0),
        invite_remaining_days: safeNumber(
          legacyCompat?.invite?.invite_remaining_days,
          0
        ),
      },
    },
  };
};

export const normalizeV3Project = (
  project: V3ProjectListItem,
  detail?: V3ProjectDetail | null
) => {
  const config = detail?.project?.config || {};
  const apiKeys = Array.isArray(project.api_keys) ? project.api_keys : [];

  return {
    id: project.id,
    name: project.name,
    key_value: project.key_value,
    key_value_list: [] as string[],
    api_key: apiKeys[0]?.key || project.key_value,
    refer: project.refer || "",
    token: 0,
    url_binding: !!project.url_binding,
    created_at: project.created_at || "",
    multi_language: !!project.multi_language,
    is_postchat: !!project.is_postchat,
    is_public: !!project.is_public,
    stats: {
      token_used: safeNumber(project.stats?.token_used, 0),
      use_count: safeNumber(project.stats?.use_count, 0),
      summary_count: safeNumber(project.stats?.summary_count, 0),
      knowledge_count: safeNumber(project.stats?.knowledge_count, 0),
    },
    prompt: project.prompt || "",
    enable_mcp: !!(config as Record<string, unknown>).mcp_enabled,
    enable_podcast: !!(config as Record<string, unknown>).podcast_enabled,
    // 项目级语言策略（全局生效；缺省 follow / zh-CN）
    language_mode:
      ((config as Record<string, unknown>).language_mode as string) || "follow",
    default_language:
      ((config as Record<string, unknown>).default_language as string) || "zh-CN",
    target_language:
      ((config as Record<string, unknown>).target_language as string) || "",
  };
};

export const normalizeV3Product = (product: V3Product) => {
  const normalizedType = product.type === 2 ? "member" : "token";
  const price = safeNumber(product.product_price, 0);
  const originalPrice = safeNumber(product.original_price, 0);

  return {
    id: product.id,
    code: product.product_code,
    type: normalizedType,
    vip_level:
      product.vip_level !== undefined && product.vip_level !== null
        ? product.vip_level
        : inferVipLevel(product),
    name: product.product_name,
    price,
    token_amount: product.type === 1 ? safeNumber(product.value, 0) : undefined,
    // 原价缺省或为 0 时退回售价（无划线价）
    original_price: originalPrice > 0 ? originalPrice : price,
    discount_desc: product.discount_desc || "",
    discount_rate: safeNumber(product.discount_rate, 0),
    discount_type: product.discount_type || "none",
    discount_start: product.discount_start,
    discount_end: product.discount_end,
    reward_product: product.reward_product || "none",
    description: product.product_desc || "",
  };
};

export const filterV3ProductsByCode = (
  products: V3Product[],
  code: "P" | "S"
) => {
  return products
    .filter((product) => {
      if (code === "P") {
        return product.type === 2 || product.product_code?.toUpperCase().startsWith("P");
      }
      return product.type === 1 || product.product_code?.toUpperCase().startsWith("S");
    })
    .map(normalizeV3Product);
};

export const normalizeV3Order = (order: V3Order) => {
  const payStatus = order.refund_status
    ? 2
    : safeNumber(order.pay_status, safeNumber(order.status, 0) > 0 ? 1 : 0);

  return {
    id: order.id,
    order_id: order.order_id,
    order_amount: safeNumber(order.order_amount, 0),
    pay_amount: safeNumber(order.pay_amount, safeNumber(order.order_amount, 0)),
    pay_type: order.pay_type || "unknown",
    pay_status: payStatus,
    product_name: order.order_subject || order.remark || "订单",
    product_count: 1,
    created_at: order.created_at,
    pay_time: order.pay_time || "",
  };
};